import { useMutationState } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";

type MutationListener<TData = unknown, TError = unknown> = (state: {
    status: 'idle' | 'pending' | 'success' | 'error';
    isPending: boolean;
    isSuccess: boolean;
    isError: boolean;
    data: TData | null;
    error: TError | null;
}) => void;

type MutationTrackerState<TData = unknown, TError = unknown> = {
    status: 'idle' | 'pending' | 'success' | 'error';
    addListener: (listener: MutationListener<TData, TError>) => () => void;
    removeListener: (listener: MutationListener<TData, TError>) => void;
    isPending: boolean;
    isSuccess: boolean;
    isError: boolean;
    data: TData | null;
    error: TError | null;
};

// Deep equality check without JSON.stringify
function isEqual(obj1: unknown, obj2: unknown): boolean {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;
    if (obj1 === null || obj2 === null) return false;
    
    const keys1 = Object.keys(obj1 as object);
    const keys2 = Object.keys(obj2 as object);
    
    if (keys1.length !== keys2.length) return false;
    
    for (const key of keys1) {
        if (!keys2.includes(key)) return false;
        if (!isEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])) {
            return false;
        }
    }
    
    return true;
}

export const useMutationTracker = <TData = unknown, TError = unknown>(key: string): MutationTrackerState<TData, TError> => {
    const listenersRef = useRef<Set<MutationListener<TData, TError>>>(new Set());
    const prevStateRef = useRef<{
        status: 'idle' | 'pending' | 'success' | 'error';
        isPending: boolean;
        isSuccess: boolean;
        isError: boolean;
        data: TData | null;
        error: TError | null;
    } | null>(null);
    const isMountedRef = useRef(false);
    
    const [currentState, setCurrentState] = useState<{
        status: 'idle' | 'pending' | 'success' | 'error';
        isPending: boolean;
        isSuccess: boolean;
        isError: boolean;
        data: TData | null;
        error: TError | null;
    }>({
        status: 'idle',
        isPending: false,
        isSuccess: false,
        isError: false,
        data: null,
        error: null
    });

    const state = useMutationState({
        filters: { mutationKey: [key] },
        select: (mutation) => {
            const latest = mutation.state;
            return {
                status: latest?.status || 'idle',
                isPending: latest?.status === 'pending',
                isSuccess: latest?.status === 'success',
                isError: latest?.status === 'error',
                data: (latest?.data as TData) ?? null,
                error: (latest?.error as TError) ?? null
            };
        }
    });

    // Get the latest mutation state
    const latestMutationState = useMemo(() => {
        return state[state.length - 1];
    }, [state]);

    // Listener management functions - memoized
    const addListener = useCallback((listener: MutationListener<TData, TError>) => {
        listenersRef.current.add(listener);
        return () => {
            listenersRef.current.delete(listener);
        };
    }, []);

    const removeListener = useCallback((listener: MutationListener<TData, TError>) => {
        listenersRef.current.delete(listener);
    }, []);

    // Notify all listeners when state changes - stable reference
    const notifyListeners = useCallback((newState: typeof currentState) => {
        listenersRef.current.forEach(listener => {
            try {
                listener(newState);
            } catch (error) {
                console.error('Error in mutation listener:', error);
            }
        });
    }, []);

    // Handle mount/unmount for StrictMode compatibility
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Update current state and notify listeners when mutation state changes
    useEffect(() => {
        if (!isMountedRef.current) return;
        
        if (latestMutationState) {
            const newState = {
                status: latestMutationState.status,
                isPending: latestMutationState.isPending,
                isSuccess: latestMutationState.isSuccess,
                isError: latestMutationState.isError,
                data: latestMutationState.data,
                error: latestMutationState.error
            };
            
            // Only update and notify if state actually changed
            if (!isEqual(newState, prevStateRef.current)) {
                prevStateRef.current = newState;
                setCurrentState(newState);
                notifyListeners(newState);
            }
        }
    }, [latestMutationState, notifyListeners]);

    // Cleanup listeners on unmount
    useEffect(() => {
        return () => {
            listenersRef.current.clear();
        };
    }, []);

    // Memoize the return object to prevent unnecessary re-renders
    return useMemo(() => ({
        ...currentState,
        addListener,
        removeListener
    }), [currentState, addListener, removeListener]);
};
